/* eslint-disable no-param-reassign */
import { Component, registerComponent } from 'js/component.js';
import { getBreakpointMediaQuery } from 'js/constants.js';
import Glide from '@glidejs/glide';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger.js';

const componentName = 'campaign-tags';

export class CampaignTags extends Component {

    // ==========
    // COMPONENT SETUP
    // ==========
    classNames = {
        glideTrack: 'glide__track',
        glideSlides: 'glide__slides',
    };

    selectors = {
        cardsContainer: `.${componentName}__cards-container`,
        cardWrapper: `.${componentName}__card-wrapper`,
    };

    elements = {
        container: this.element.querySelector(`.${componentName}__container`),
        filterToggles: this.element.querySelectorAll(`.${componentName}__filter-toggle input`),
        cardsContainerWrapper: this.element.querySelector(`.${componentName}__cards-container-wrapper`),
        cardsContainer: this.element.querySelector(this.selectors.cardsContainer),
        cards: this.element.querySelectorAll(this.selectors.cardWrapper),
        bottomLinks: this.element.querySelectorAll(`.${componentName}__bottom-link`),
    };

    state = {
        numCardsPerTag: 6,
        resourcePath: this.elements.container.dataset.resourcePath,
        disableFetch: this.elements.container.dataset.disableFetch === 'true',
        // [LEGACY BREAKPOINT: max-width 48em -> getBreakpointMediaQuery('down', 'small')]
        matchMobile: getBreakpointMediaQuery('down', 'small'),
        gsapTimeline: null,
        glideInstance: null,
    };

    // ==========
    // FETCH DATA
    // ==========
    fetchContent = async () => {
        const response = await fetch(`${this.state.resourcePath}.html`);
        if (response.ok) {
            const div = document.createElement('div');
            div.innerHTML = await response.text();
            const newCardsContainer = div.querySelector(this.selectors.cardsContainer);
            this.elements.cardsContainer.innerHTML = newCardsContainer.innerHTML;
            this.elements.cards = this.element.querySelectorAll(this.selectors.cardWrapper);
        }
    };

    // ==========
    // ANIMATION
    // ==========
    addAnimation = () => {
        if (this.state.matchMobile.matches) {
            this.mountCarousel();
        } else {
            this.initGsap();
        }
    };

    removeAnimation = () => {
        this.killGsap();
        this.destroyCarousel();
    };

    // Desktop animation
    initGsap = () => {
        // Animation within multi-column layout on Safari has issues
        if (window.navigator.vendor?.includes('Apple')) {
            return;
        }
        gsap.registerPlugin(ScrollTrigger);
        this.state.gsapTimeline = gsap.timeline({
            scrollTrigger: {
                trigger: this.elements.cardsContainer,
                scrub: 0.5,
                end: 'top 40%',
            },
        });
        this.state.gsapTimeline.pause(Infinity);
        this.element.querySelectorAll(this.selectors.cardWrapper).forEach((card) => {
            this.state.gsapTimeline.from(card, { y: 150 }, '-=80%');
        });
    };

    killGsap = () => {
        if (this.state.gsapTimeline) {
            this.state.gsapTimeline.pause(Infinity); // Set animation to end state
            this.state.gsapTimeline.kill();
        }
    };

    // Mobile carousel
    mountCarousel = () => {
        // These classes apply glide styling
        this.elements.cardsContainerWrapper.classList.add(this.classNames.glideTrack);
        this.elements.cardsContainer.classList.add(this.classNames.glideSlides);

        this.state.glideInstance = new Glide(this.elements.container, {
            type: 'carousel',
            perView: 1,
            gap: 8,
        });
        this.state.glideInstance.mount();
    };

    destroyCarousel = () => {
        if (this.state.glideInstance) {
            this.state.glideInstance.destroy();
            this.state.glideInstance = null;
        }
        if (!this.state.matchMobile.matches) {
            this.elements.cardsContainerWrapper.classList.remove(this.classNames.glideTrack);
            this.elements.cardsContainer.classList.remove(this.classNames.glideSlides);
        }
    };

    // Re-init animation on mobile/desktop switch
    handleMatchMobileChange = () => {
        this.removeAnimation();
        this.addAnimation();
    };

    /**
     * Category selection
     * @param {string?} tagIndex
     */
    showCategory = (tagIndex) => {

        let matchingCards = [];
        if (tagIndex == null) {
            // No tag selected, show first 6 cards
            matchingCards = [...this.elements.cards].slice(0, this.state.numCardsPerTag);
        } else {
            // Tag is selected
            matchingCards = [...this.elements.cards].filter((card) => card.dataset.tagIndex === tagIndex);
            if (matchingCards.length > this.state.numCardsPerTag) {
                // Remove sixth card to display authored category card instead
                matchingCards.splice(this.state.numCardsPerTag - 1, 1);
            }
        }

        matchingCards.forEach((card) => {
            card.hidden = false;
        });

        // Replace content and re-init animation
        this.removeAnimation();
        this.elements.cardsContainer.replaceChildren(...matchingCards);
        this.addAnimation();

        // Only show bottom link if it matches the selected tag
        this.elements.bottomLinks.forEach((link) => {
            link.hidden = link.dataset.tagIndex !== tagIndex;
        });
    };

    /** @param {Event} ev */
    handleFilterToggle = (ev) => {
        const checkbox = ev.currentTarget;

        // Uncheck others
        this.elements.filterToggles.forEach((el) => {
            if (el !== checkbox) {
                el.checked = false;
            }
        });

        this.showCategory(checkbox.checked ? checkbox.value : null);
    };

    // ==========
    // ADD EVENT LISTENERS
    // ==========
    addEventListeners = () => {
        this.elements.filterToggles.forEach((el) => el.addEventListener('change', this.handleFilterToggle));
        this.state.matchMobile.addEventListener('change', this.handleMatchMobileChange);
    };

    // ==========
    // INITIALIZE COMPONENT
    // ==========
    init = async () => {
        if (!this.state.disableFetch) {
            await this.fetchContent();
        }
        this.showCategory();
        this.addEventListeners();
    };
}

registerComponent(`.${componentName}`, CampaignTags);
